/**
 * Parses Navigator agent responses containing markdown + embedded component directives.
 *
 * Directives follow the pattern:
 *   :::directive-name{key="value" key2=value2}
 *   content (YAML-like or markdown table)
 *   :::
 *
 * Inline directives: :::token{mint="..." symbol="SOL" price="$142"}:::
 *
 * Returns an array of segments:
 *   { type: 'markdown', content: '...' }
 *   { type: 'metric-cards', props: { cards: [...] } }
 *   { type: 'chart', props: { type: 'line', title: '...', ... } }
 *   { type: 'comparison-table', props: { title: '...', content: '...' } }
 *   { type: 'data-table', props: { title: '...', sortable: true, content: '...' } }
 *   { type: 'token', props: { mint: '...', symbol: '...', price: '...', change: '...' } }
 *   { type: 'callout', props: { type: 'info', content: '...' } }
 *   { type: 'suggestions', props: { items: [...] } }
 */

export function useDirectiveParser() {
    /**
     * Parse a complete response string into segments.
     */
    function parse(text) {
        if (!text) return [{ type: 'markdown', content: '' }];

        const segments = [];
        let remaining = text;

        while (remaining.length > 0) {
            // Look for the next directive opening
            const directiveMatch = remaining.match(/^([\s\S]*?):::([\w-]+)(\{[^}]*\})?(?:::)?/);

            if (!directiveMatch) {
                // No more directives — rest is markdown
                if (remaining.trim()) {
                    segments.push({ type: 'markdown', content: remaining });
                }
                break;
            }

            const [fullMatch, before, directiveName, attrsStr] = directiveMatch;

            // Add preceding markdown
            if (before.trim()) {
                segments.push({ type: 'markdown', content: before });
            }

            // Check if it's an inline directive (:::token{...}:::)
            const inlineMatch = remaining.match(new RegExp(`^[\\s\\S]*?:::${directiveName}(\\{[^}]*\\}):::`, 'm'));
            if (inlineMatch && inlineMatch.index === directiveMatch.index) {
                const attrs = parseAttributes(attrsStr);
                segments.push({
                    type: directiveName,
                    props: attrs,
                    inline: true,
                });
                remaining = remaining.slice(inlineMatch[0].length);
                continue;
            }

            // Block directive — find the closing :::
            const afterOpening = remaining.slice(fullMatch.length);
            const closingIndex = afterOpening.indexOf('\n:::');

            if (closingIndex === -1) {
                // No closing found — treat as markdown
                segments.push({ type: 'markdown', content: fullMatch });
                remaining = afterOpening;
                continue;
            }

            const directiveContent = afterOpening.slice(0, closingIndex).trim();
            const attrs = parseAttributes(attrsStr);

            // Parse based on directive type
            const segment = parseDirective(directiveName, attrs, directiveContent);
            if (segment) {
                segments.push(segment);
            }

            // Skip past closing :::
            remaining = afterOpening.slice(closingIndex + 4); // 4 = '\n:::'.length
        }

        return segments.length > 0 ? segments : [{ type: 'markdown', content: text }];
    }

    /**
     * Parse {key="value" key2=value2} attribute strings.
     */
    function parseAttributes(attrStr) {
        if (!attrStr) return {};

        // Remove surrounding braces
        const inner = attrStr.replace(/^\{|\}$/g, '');
        const attrs = {};

        // Match key="value" or key=value patterns
        const regex = /(\w+)=(?:"([^"]*)"|(\S+))/g;
        let match;
        while ((match = regex.exec(inner)) !== null) {
            const key = match[1];
            const value = match[2] !== undefined ? match[2] : match[3];
            // Type coerce booleans
            if (value === 'true') attrs[key] = true;
            else if (value === 'false') attrs[key] = false;
            else attrs[key] = value;
        }

        return attrs;
    }

    /**
     * Parse a specific directive type into a segment.
     */
    function parseDirective(name, attrs, content) {
        switch (name) {
            case 'metric-cards':
                return { type: 'metric-cards', props: { cards: parseMetricCards(content) } };

            case 'chart':
                return { type: 'chart', props: { ...attrs, ...parseChartData(content) } };

            case 'comparison-table':
                return { type: 'comparison-table', props: { ...attrs, content } };

            case 'data-table':
                return { type: 'data-table', props: { ...attrs, content } };

            case 'callout':
                return { type: 'callout', props: { ...attrs, content } };

            case 'suggestions':
                return { type: 'suggestions', props: { items: parseSuggestions(content) } };

            default:
                return { type: name, props: { ...attrs, content } };
        }
    }

    /**
     * Parse metric-cards YAML-like content into card objects.
     */
    function parseMetricCards(content) {
        const cards = [];
        let current = null;

        for (const line of content.split('\n')) {
            const trimmed = line.trim();

            if (trimmed.startsWith('- label:')) {
                if (current) cards.push(current);
                current = { label: extractValue(trimmed.replace('- label:', '')) };
            } else if (current && trimmed.startsWith('value:')) {
                current.value = extractValue(trimmed.replace('value:', ''));
            } else if (current && trimmed.startsWith('change:')) {
                current.change = extractValue(trimmed.replace('change:', ''));
            } else if (current && trimmed.startsWith('compare_label:')) {
                current.compare_label = extractValue(trimmed.replace('compare_label:', ''));
            } else if (current && trimmed.startsWith('sentiment:')) {
                current.sentiment = extractValue(trimmed.replace('sentiment:', ''));
            } else if (current && trimmed.startsWith('unit:')) {
                current.unit = extractValue(trimmed.replace('unit:', ''));
            }
        }

        if (current) cards.push(current);
        return cards;
    }

    /**
     * Parse chart YAML-like data.
     */
    function parseChartData(content) {
        const result = { labels: [], datasets: [], data: [], colors: [] };

        try {
            // Try to extract labels line
            const labelsMatch = content.match(/^labels:\s*\[([^\]]*)\]/m);
            if (labelsMatch) {
                result.labels = parseArray(labelsMatch[1]);
            }

            // Extract standalone data array (for doughnut)
            const dataMatch = content.match(/^data:\s*\[([^\]]*)\]/m);
            if (dataMatch) {
                result.data = parseArray(dataMatch[1]).map(Number);
            }

            // Extract standalone colors array
            const colorsMatch = content.match(/^colors:\s*\[([^\]]*)\]/m);
            if (colorsMatch) {
                result.colors = parseArray(colorsMatch[1]);
            }

            // Extract datasets
            const datasetBlocks = content.split(/\n\s*-\s*label:\s*/);
            if (datasetBlocks.length > 1) {
                for (let i = 1; i < datasetBlocks.length; i++) {
                    const block = datasetBlocks[i];
                    const dataset = { label: '' };

                    // First line is the label value
                    const lines = block.split('\n');
                    dataset.label = extractValue(lines[0]);

                    for (const line of lines.slice(1)) {
                        const trimmed = line.trim();
                        if (trimmed.startsWith('data:')) {
                            const arrMatch = trimmed.match(/\[([^\]]*)\]/);
                            if (arrMatch) {
                                dataset.data = parseArray(arrMatch[1]).map(Number);
                            }
                        } else if (trimmed.startsWith('color:')) {
                            dataset.color = extractValue(trimmed.replace('color:', ''));
                        } else if (trimmed.startsWith('fill:')) {
                            dataset.fill = extractValue(trimmed.replace('fill:', '')) === 'true';
                        } else if (trimmed.startsWith('yAxis:')) {
                            dataset.yAxis = extractValue(trimmed.replace('yAxis:', ''));
                        }
                    }

                    result.datasets.push(dataset);
                }
            }
        } catch (e) {
            console.warn('Failed to parse chart data:', e);
        }

        return result;
    }

    /**
     * Parse suggestion items from content.
     */
    function parseSuggestions(content) {
        return content
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.startsWith('-'))
            .map(line => {
                const text = line.replace(/^-\s*/, '').replace(/^["']|["']$/g, '');
                return text;
            });
    }

    /**
     * Extract a value from a YAML-like "key: value" or '"value"' string.
     */
    function extractValue(str) {
        return str.trim().replace(/^["']|["']$/g, '');
    }

    /**
     * Parse a comma-separated array string into values.
     */
    function parseArray(str) {
        return str.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
    }

    /**
     * Incrementally parse streaming text, yielding complete segments.
     * Returns { segments, buffer } where buffer is unparsed remainder.
     */
    function parseIncremental(buffer) {
        const segments = [];
        let remaining = buffer;

        while (true) {
            // Look for complete block directives
            const blockMatch = remaining.match(/([\s\S]*?):::([\w-]+)(\{[^}]*\})?\n([\s\S]*?)\n:::/);

            if (!blockMatch) {
                // Check for inline directives
                const inlineMatch = remaining.match(/([\s\S]*?):::([\w-]+)(\{[^}]*\}):::/);
                if (inlineMatch) {
                    const [full, before, name, attrsStr] = inlineMatch;
                    if (before.trim()) {
                        segments.push({ type: 'markdown', content: before });
                    }
                    segments.push({
                        type: name,
                        props: parseAttributes(attrsStr),
                        inline: true,
                    });
                    remaining = remaining.slice(full.length);
                    continue;
                }

                break;
            }

            const [full, before, name, attrsStr, content] = blockMatch;

            if (before.trim()) {
                segments.push({ type: 'markdown', content: before });
            }

            const attrs = parseAttributes(attrsStr);
            const segment = parseDirective(name, attrs, content.trim());
            if (segment) segments.push(segment);

            remaining = remaining.slice(full.length);
        }

        return { segments, buffer: remaining };
    }

    return {
        parse,
        parseIncremental,
        parseAttributes,
        parseMetricCards,
        parseChartData,
        parseSuggestions,
    };
}
